/**
 * Courier team permissions.
 *
 * A courier business has exactly one owner (the login that created the
 * courier account). Everyone else is a team member with an explicit list of
 * abilities. Presets exist so a courier never has to reason about 24
 * checkboxes, but the stored authority is always the permission array — the
 * role is a label plus a starting point.
 */

export type TeamPermission =
  | "jobs.view" | "jobs.bid" | "jobs.accept" | "jobs.decline"
  | "shipments.view" | "shipments.edit" | "shipments.edit_price"
  | "shipments.update_status" | "shipments.confirm_collection" | "shipments.confirm_delivery" | "shipments.cancel"
  | "trips.view" | "trips.manage" | "trips.assign"
  | "fleet.view" | "fleet.manage"
  | "documents.view" | "documents.upload"
  | "messages.view" | "messages.send"
  | "contacts.view" | "contacts.manage"
  | "storefront.view" | "storefront.manage"
  | "routes.manage" | "pricing.manage"
  | "promotions.manage"
  | "analytics.view"
  | "finance.view" | "finance.manage"
  | "payments.view" | "payments.record"
  | "customers.contact_details"
  | "team.manage";

export interface PermissionGroup {
  key: string;
  labelKey: string;
  fallback: string;
  permissions: { key: TeamPermission; fallback: string; sensitive?: boolean }[];
}

/**
 * Only the choices a courier actually has to make are shown. Pure "view"
 * abilities are implied by the ability that needs them (see IMPLIES), so
 * nobody has to tick "See trips" before "Create and edit trips".
 */
export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    key: "work", labelKey: "courierTeam.groups.work", fallback: "Finding work",
    permissions: [
      { key: "jobs.view", fallback: "See open jobs and matches" },
      { key: "jobs.bid", fallback: "Send offers / bids" },
      { key: "jobs.accept", fallback: "Accept jobs" },
      { key: "jobs.decline", fallback: "Decline jobs", sensitive: true },
    ],
  },
  {
    key: "operations", labelKey: "courierTeam.groups.operations", fallback: "Day-to-day operations",
    permissions: [
      { key: "shipments.view", fallback: "See shipments" },
      { key: "shipments.edit", fallback: "Edit shipment details (addresses, sender/receiver, notes)" },
      { key: "shipments.edit_price", fallback: "Edit shipment and quote prices", sensitive: true },
      { key: "shipments.update_status", fallback: "Move shipments forward (collected, in transit, delivered)" },
      { key: "shipments.confirm_collection", fallback: "Confirm collection and take signatures" },
      { key: "shipments.confirm_delivery", fallback: "Confirm delivery and take signatures" },
      { key: "shipments.cancel", fallback: "Cancel a shipment", sensitive: true },
      { key: "trips.manage", fallback: "Create and edit trips" },
      { key: "trips.assign", fallback: "Assign vehicles and drivers to trips", sensitive: true },
      { key: "fleet.manage", fallback: "Manage vehicles and drivers" },
      { key: "documents.upload", fallback: "Upload shipment documents" },
      { key: "payments.record", fallback: "Record customer payments (paid, pay at delivery, part-payments)", sensitive: true },
    ],
  },
  {
    key: "customers", labelKey: "courierTeam.groups.customers", fallback: "Customers and messages",
    permissions: [
      { key: "messages.send", fallback: "Read and reply to customer messages" },
      { key: "contacts.manage", fallback: "Add and edit contacts and enquiries" },
      { key: "customers.contact_details", fallback: "See customer phone numbers and emails", sensitive: true },
    ],
  },
  {
    key: "growth", labelKey: "courierTeam.groups.growth", fallback: "Marketing and growth",
    permissions: [
      { key: "storefront.manage", fallback: "Edit and publish the storefront" },
      { key: "promotions.manage", fallback: "Create promotions" },
      { key: "analytics.view", fallback: "See analytics" },
    ],
  },
  {
    key: "business", labelKey: "courierTeam.groups.business", fallback: "Business setup and money",
    permissions: [
      { key: "routes.manage", fallback: "Edit routes" },
      { key: "pricing.manage", fallback: "Edit pricing", sensitive: true },
      { key: "finance.view", fallback: "See prices and earnings", sensitive: true },
      { key: "finance.manage", fallback: "Record business expenses", sensitive: true },
      { key: "team.manage", fallback: "Manage team members", sensitive: true },
    ],
  },
];

/** An ability always carries the read access it needs to be usable. */
const IMPLIES: Partial<Record<TeamPermission, TeamPermission[]>> = {
  "jobs.bid": ["jobs.view"],
  "jobs.accept": ["jobs.view"],
  "jobs.decline": ["jobs.view"],
  "shipments.edit": ["shipments.view"],
  // Never imply finance.view — editing a price must not grant Finance visibility.
  "shipments.edit_price": ["shipments.view"],
  "shipments.update_status": ["shipments.view"],
  "shipments.confirm_collection": ["shipments.view", "documents.view"],
  "shipments.confirm_delivery": ["shipments.view", "documents.view"],
  "shipments.cancel": ["shipments.view"],
  "trips.manage": ["trips.view"],
  "trips.assign": ["trips.view", "fleet.view"],
  "fleet.manage": ["fleet.view"],
  "documents.upload": ["documents.view"],
  "messages.send": ["messages.view"],
  "contacts.manage": ["contacts.view"],
  "storefront.manage": ["storefront.view"],
  "finance.manage": ["finance.view"],
  "payments.record": ["payments.view", "shipments.view"],
};

/** Expands the ticked choices into the full stored permission list. */
export function expandPermissions(selected: TeamPermission[]): TeamPermission[] {
  const out = new Set<TeamPermission>(selected);
  for (const p of selected) for (const dep of IMPLIES[p] ?? []) out.add(dep);
  return [...out];
}

export const ALL_PERMISSIONS: TeamPermission[] = [
  "jobs.view", "jobs.bid", "jobs.accept", "jobs.decline",
  "shipments.view", "shipments.edit", "shipments.edit_price",
  "shipments.update_status", "shipments.confirm_collection", "shipments.confirm_delivery", "shipments.cancel",
  "trips.view", "trips.manage", "trips.assign",
  "fleet.view", "fleet.manage",
  "documents.view", "documents.upload",
  "messages.view", "messages.send",
  "contacts.view", "contacts.manage",
  "storefront.view", "storefront.manage",
  "routes.manage", "pricing.manage", "promotions.manage",
  "analytics.view", "finance.view", "finance.manage",
  "payments.view", "payments.record",
  "customers.contact_details", "team.manage",
];

export type TeamRole =
  | "admin" | "dispatch" | "driver" | "confirmations" | "marketing" | "finance" | "read_only" | "custom";

export const ROLE_PRESETS: Record<Exclude<TeamRole, "custom">, TeamPermission[]> = {
  admin: [...ALL_PERMISSIONS],
  dispatch: expandPermissions([
    "jobs.view", "jobs.bid", "jobs.accept",
    "shipments.update_status", "shipments.confirm_collection",
    "trips.manage", "trips.assign", "documents.upload", "fleet.manage",
    "messages.send", "contacts.manage", "customers.contact_details",
  ]),
  driver: expandPermissions([
    "trips.view", "shipments.view", "shipments.update_status",
    "shipments.confirm_collection", "shipments.confirm_delivery",
    "documents.view", "payments.view", "customers.contact_details",
  ]),
  confirmations: expandPermissions([
    "shipments.update_status", "shipments.confirm_collection",
    "documents.upload", "messages.send", "customers.contact_details",
  ]),
  marketing: expandPermissions([
    "storefront.manage", "contacts.manage", "promotions.manage", "analytics.view",
  ]),
  finance: expandPermissions([
    "shipments.view", "trips.view", "analytics.view", "finance.view", "finance.manage",
  ]),
  read_only: [
    "jobs.view", "shipments.view", "trips.view", "documents.view",
    "messages.view", "contacts.view", "storefront.view",
  ],
};

export const ROLE_LABELS: Record<TeamRole, string> = {
  admin: "Admin (full access)",
  dispatch: "Dispatch",
  driver: "Driver",
  confirmations: "Confirmations",
  marketing: "Marketing",
  finance: "Finance",
  read_only: "Read only",
  custom: "Custom",
};

export const ROLE_ORDER: TeamRole[] = [
  "admin", "dispatch", "driver", "confirmations", "marketing", "finance", "read_only", "custom",
];

export function permissionsForRole(role: TeamRole): TeamPermission[] {
  if (role === "custom") return [];
  return [...ROLE_PRESETS[role]];
}

/** Same set of permissions, order-insensitive — used to detect fine tuning. */
export function matchesPreset(role: TeamRole, permissions: string[]): boolean {
  if (role === "custom") return false;
  const preset = ROLE_PRESETS[role];
  return preset.length === permissions.length && preset.every((p) => permissions.includes(p));
}

